
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ShoppingList, ShoppingListItem, CanonicalProduct, Category, User, Brand, Offer, Retailer, RetailerProduct, NearbyStore
} from '../shared/types';
import {
    getShoppingListsByUser, saveShoppingList,
    getShoppingListItems, saveShoppingListItem,
    getCategories, getBrands, getRetailers, deleteShoppingListItem,
    searchProducts, getProductOffers, getCanonicalProduct
} from '../services/db';
import { generatePlan, resolveStoreFilter, StoreFilter } from '../services/optimizer';
import { loadActiveProspektOffers, isProspektOfferValidOn, todayIsoDate } from '../services/prospekt-offers';
import type { ProspektOffer } from '../shared/prospekt-types';
import ProspektOfferSheet from './ProspektOfferSheet';
import { useLanguage } from '../services/LanguageContext';
import { readPackageSize, formatPackageSize } from '../shared/package-size';
import { l1IdOf } from '../services/category-nav';
import CategoryTiles from './CategoryTiles';
import CategoryFilterBar from './CategoryFilterBar';
import CategoryFilterDropdown from './CategoryFilterDropdown';
import CategoryBreadcrumb from './CategoryBreadcrumb';
import OutOfRadiusModal from './OutOfRadiusModal';
import { locationApi } from '../services/location';

interface DashboardProps {
  currentUser: User | null;
  onUpdateUser?: (user: User) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ currentUser, onUpdateUser }) => {
    const { t } = useLanguage();
    const [lists, setLists] = useState<ShoppingList[]>([]);
    const [selectedList, setSelectedList] = useState<ShoppingList | null>(null);
    const [listItems, setListItems] = useState<ShoppingListItem[]>([]);
    // `products` holds only the currently loaded search-result pages (≤ a few
    // hundred), never the whole catalogue. `productsById` is a small cache so
    // shopping-list items render even when they're not in the current results.
    const [products, setProducts] = useState<CanonicalProduct[]>([]);
    const [productsById, setProductsById] = useState<Record<string, CanonicalProduct>>({});
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [retailers, setRetailers] = useState<Retailer[]>([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState<string>('All');
    const [activeBrands, setActiveBrands] = useState<string[]>([]);
    const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
    const brandDropdownRef = useRef<HTMLDivElement>(null);
    const [activeRetailers, setActiveRetailers] = useState<string[]>([]);
    const [retailerDropdownOpen, setRetailerDropdownOpen] = useState(false);
    const retailerDropdownRef = useRef<HTMLDivElement>(null);
    const [sortBy, setSortBy] = useState<'brand' | 'price'>('brand');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);

    // UI states for new features
    const [detailProduct, setDetailProduct] = useState<CanonicalProduct | null>(null);
    const [productOffers, setProductOffers] = useState<{offer: Offer, rp: RetailerProduct, retailer: Retailer}[]>([]);
    const [isListDrawerOpen, setIsListDrawerOpen] = useState(false);
    const [isOptimizing, setIsOptimizing] = useState(false);
    // #287: currently-valid promo leaflet offers per canonical product — drives
    // the deal badge on product tiles; tapping opens the details bottom sheet.
    const [prospektByProduct, setProspektByProduct] = useState<Map<string, ProspektOffer[]>>(new Map());
    const [prospektSheet, setProspektSheet] = useState<ProspektOffer | null>(null);

    const navigate = useNavigate();

    // A search/browse is "active" once there's a query or a filter; until then
    // the dashboard shows an empty prompt and fetches nothing.
    const isQueryActive = searchTerm.trim() !== '' || activeCategory !== 'All' || activeBrands.length > 0 || activeRetailers.length > 0;

    // The L1 department of the active category (its path root) — drives the
    // filter chip bar and breadcrumb (#63).
    const activeL1Id = useMemo(
        () => (activeCategory !== 'All' ? l1IdOf(categories, activeCategory) : null),
        [categories, activeCategory],
    );

    // Close brand/retailer dropdowns when clicking outside.
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target as Node)) {
                setBrandDropdownOpen(false);
            }
            if (retailerDropdownRef.current && !retailerDropdownRef.current.contains(e.target as Node)) {
                setRetailerDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        loadInitialData();
    }, [currentUser]);

    // #287: load promo leaflet offers once — degrades to no badges when the
    // feature flag is off or the read API is unreachable (graceful fallback).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const offers = await loadActiveProspektOffers();
            if (cancelled || offers.length === 0) return;
            const today = todayIsoDate();
            const byProduct = new Map<string, ProspektOffer[]>();
            for (const o of offers) {
                if (!o.isPromo || o.status !== 'active' || !isProspektOfferValidOn(o, today)) continue;
                const list = byProduct.get(o.canonicalProductId) ?? [];
                list.push(o);
                byProduct.set(o.canonicalProductId, list);
            }
            // Cheapest first, so the badge opens the best deal.
            for (const list of byProduct.values()) list.sort((a, b) => a.priceCents - b.priceCents);
            setProspektByProduct(byProduct);
        })();
        return () => { cancelled = true; };
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        const [c, b, r] = await Promise.all([getCategories(), getBrands(), getRetailers()]);
        setCategories(c);
        setBrands(b);
        setRetailers(r);

        if (currentUser) {
            const l = await getShoppingListsByUser(currentUser.id);
            setLists(l);
            if (l.length > 0) {
                setSelectedList(l[0]);
                const items = await getShoppingListItems(l[0].id);
                setListItems(items);
                hydrateProducts(items.map(i => i.canonicalProductId));
            }
        }
        setLoading(false);
    };

    // Fetch any list-item products we don't already have cached, so the drawer
    // and list rendering always have product details.
    const hydrateProducts = async (ids: string[]) => {
        const missing = [...new Set(ids)].filter(id => id && !productsById[id]);
        if (!missing.length) return;
        const fetched = await Promise.all(missing.map(id => getCanonicalProduct(id).catch(() => null)));
        setProductsById(prev => {
            const next = { ...prev };
            fetched.forEach(p => { if (p) next[p.id] = p; });
            return next;
        });
    };

    const mergeIntoCache = (items: CanonicalProduct[]) => {
        setProductsById(prev => {
            const next = { ...prev };
            items.forEach(p => { next[p.id] = p; });
            return next;
        });
    };

    // Run a search for the given page. `append` adds to the current results
    // (Load more); otherwise it replaces them (new query/filter/sort).
    const runSearch = async (targetPage: number, append: boolean) => {
        setSearching(true);
        try {
            const res = await searchProducts({
                q: searchTerm.trim() || undefined,
                category: activeCategory !== 'All' ? activeCategory : undefined,
                brands: activeBrands.length ? activeBrands : undefined,
                retailers: activeRetailers.length ? activeRetailers : undefined,
                page: targetPage,
                limit: 50,
                sort: sortBy === 'price' ? 'price' : 'name',
            });
            setProducts(prev => append ? [...prev, ...res.items] : res.items);
            mergeIntoCache(res.items);
            setTotal(res.total);
            setPage(res.page);
            setHasMore(res.hasMore);
        } catch (e) {
            console.error('Product search failed', e);
        } finally {
            setSearching(false);
        }
    };

    // Debounced search whenever the query, filters, or sort change.
    useEffect(() => {
        if (loading) return;
        if (!isQueryActive) {
            setProducts([]);
            setTotal(0);
            setHasMore(false);
            setPage(1);
            return;
        }
        const handle = setTimeout(() => runSearch(1, false), 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, activeCategory, activeBrands, activeRetailers, sortBy, loading]);

    const handleAddItem = async (productId: string) => {
        if (!currentUser) {
            navigate('/login');
            return;
        }

        let currentListId = selectedList?.id;
        if (!currentListId) {
            const newList: ShoppingList = {
                id: Math.random().toString(36).substr(2, 9),
                name: 'Main Grocery List',
                userId: currentUser.id,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            await saveShoppingList(newList);
            setLists([newList]);
            setSelectedList(newList);
            currentListId = newList.id;
        }

        const existing = listItems.find(i => i.canonicalProductId === productId);
        if (existing) {
            const updated = { ...existing, quantity: existing.quantity + 1, updatedAt: new Date().toISOString() };
            await saveShoppingListItem(updated);
            setListItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        } else {
            const newItem: ShoppingListItem = {
                id: Math.random().toString(36).substr(2, 9),
                listId: currentListId,
                canonicalProductId: productId,
                quantity: 1,
                preferExactPackMatch: 0,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            await saveShoppingListItem(newItem);
            setListItems(prev => [...prev, newItem]);
        }
    };

    const handleUpdateQuantity = async (itemId: string, delta: number) => {
        const item = listItems.find(i => i.id === itemId);
        if (!item) return;
        const newQty = item.quantity + delta;
        if (newQty <= 0) {
            await deleteShoppingListItem(itemId);
            setListItems(prev => prev.filter(i => i.id !== itemId));
        } else {
            const updated = { ...item, quantity: newQty, updatedAt: new Date().toISOString() };
            await saveShoppingListItem(updated);
            setListItems(prev => prev.map(i => i.id === itemId ? updated : i));
        }
    };

    const handleViewDeals = async (product: CanonicalProduct) => {
        setDetailProduct(product);
        setProductOffers([]);
        try {
            // One round-trip for just this product's offers (was: load every offer).
            const { offers, retailerProducts: rps, retailers: rets } = await getProductOffers(product.id);
            const relevant = offers.map(o => {
                const rp = rps.find(r => r.id === o.retailerProductId)!;
                return {
                    offer: o,
                    rp,
                    retailer: rets.find(r => r.id === rp?.retailerId)!,
                };
            }).filter(x => x.rp).sort((a, b) => a.offer.priceCents - b.offer.priceCents);
            setProductOffers(relevant);
        } catch (e) {
            console.error('Failed to load product offers', e);
            setProductOffers([]);
        }
    };

    // #26 out-of-radius prompt state.
    const [oorOpen, setOorOpen] = useState(false);
    const [oorDistance, setOorDistance] = useState<number | null>(null);

    const runPlan = async (oneOffStores?: NearbyStore[]) => {
        if (!selectedList || listItems.length === 0) return;
        setIsOptimizing(true);
        try {
            // #147: restrict the plan to the stores the user actually shops at.
            // A #26 one-off "stores near my home" selection wins for this run;
            // otherwise use the saved favourites (∩ stores with pricing data).
            // No priced stores → resolveStoreFilter returns undefined and the
            // optimiser keeps its current unfiltered behaviour.
            const storeFilter: StoreFilter | undefined = oneOffStores
                ? { preferredStoreIds: oneOffStores.map(s => s.storeId), fallbackStoreIds: [] }
                : resolveStoreFilter(currentUser?.favouriteStores, currentUser?.nearbyStores);
            const result = await generatePlan(selectedList.id, listItems, 3, storeFilter);
            if (result) {
                navigate(`/plan/${result.plan.id}`);
            }
        } catch (err) {
            console.error("Optimization failed", err);
        } finally {
            setIsOptimizing(false);
        }
    };

    const handleOptimize = async () => {
        if (!selectedList || listItems.length === 0) return;
        // #26: when a consented user optimises far from all their favourite
        // stores, prompt before running. The check is best-effort — any failure
        // falls through to a normal optimisation rather than blocking the user.
        if (currentUser && currentUser.locationConsentGiven === true) {
            try {
                const { outOfRadius, distanceToNearestFavouriteKm } = await locationApi.outOfRadiusCheck();
                if (outOfRadius) {
                    setOorDistance(distanceToNearestFavouriteKm);
                    setOorOpen(true);
                    return;
                }
            } catch { /* non-blocking */ }
        }
        runPlan();
    };

    // Filter chips: counts were dropped (they required scanning the whole
    // catalogue). Show a manageable, alphabetical slice; the search box is the
    // primary discovery tool.
    const topCategories = useMemo(
        () => [...categories].sort((a, b) => a.name.localeCompare(b.name)).slice(0, 8),
        [categories]
    );
    const sortedBrands = useMemo(
        () => [...brands].sort((a, b) => a.name.localeCompare(b.name)),
        [brands]
    );

    const toggleBrand = (brandId: string) => {
        setActiveBrands(prev =>
            prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId]
        );
    };

    const brandButtonLabel = () => {
        if (activeBrands.length === 0) return t('filter.allBrands');
        if (activeBrands.length === 1) return brands.find(b => b.id === activeBrands[0])?.name ?? '1';
        return `${activeBrands.length} Marken`;
    };

    const sortedRetailers = useMemo(
        () => [...retailers].sort((a, b) => a.name.localeCompare(b.name)),
        [retailers]
    );

    const toggleRetailer = (retailerId: string) => {
        setActiveRetailers(prev =>
            prev.includes(retailerId) ? prev.filter(id => id !== retailerId) : [...prev, retailerId]
        );
    };

    const retailerButtonLabel = () => {
        if (activeRetailers.length === 0) return t('filter.allRetailers');
        if (activeRetailers.length === 1) return retailers.find(r => r.id === activeRetailers[0])?.name ?? '1';
        return `${activeRetailers.length}`;
    };

    // Group the loaded result pages by category for display. Ordering/sorting
    // is decided server-side; this only sections the already-ordered results.
    const groupedProducts = useMemo(() => {
        const groups: Record<string, CanonicalProduct[]> = {};
        products.forEach(p => {
            const catName = categories.find(c => c.id === p.categoryId)?.name || 'Uncategorized';
            if (!groups[catName]) groups[catName] = [];
            groups[catName].push(p);
        });
        return groups;
    }, [products, categories]);

    // Price label for a product card: "ab €X.XX" or "no current price".
    const renderPrice = (p: CanonicalProduct) =>
        p.minPriceCents != null
            ? <span className="text-sm font-black text-emerald-500">{t('product.priceFrom')} €{(p.minPriceCents / 100).toFixed(2)}</span>
            : <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{t('product.noPrice')}</span>;

    // #287: promo badge for a product with a currently-valid leaflet offer.
    // Shows the discount label when the leaflet carries one, "🏷️ Angebot"
    // otherwise; tapping opens the offer-details bottom sheet.
    const renderProspektBadge = (productId: string) => {
        const offers = prospektByProduct.get(productId);
        if (!offers?.length) return null;
        const best = offers[0];
        return (
            <button
                type="button"
                data-testid={`prospekt-badge-${productId}`}
                onClick={(e) => { e.stopPropagation(); setProspektSheet(best); }}
                className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-lg hover:bg-amber-500/20 transition-all"
            >
                <span aria-hidden="true">🏷️</span> {best.discountLabel || t('prospekt.badge')}
            </button>
        );
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-48 space-y-6">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin shadow-xl shadow-emerald-500/20"></div>
            <p className="text-emerald-500 font-bold uppercase tracking-widest text-sm">{t('catalog.loading')}</p>
        </div>
    );

    return (
        <div className="motion-safe:animate-fadeIn relative">
            <div className="hero-gradient absolute top-0 left-0 w-full h-[600px] pointer-events-none"></div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center space-y-12">
                <div className="space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/5 dark:bg-emerald-500/5 border border-emerald-500/20 rounded-full text-xs font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                        <i className="fas fa-sparkles"></i> {currentUser ? `${t('hero.badge').split(' ')[0]}, ${currentUser.email.split('@')[0]}` : t('hero.badge')}
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black text-slate-900 dark:text-white leading-[1.1] tracking-tight">
                        {t('hero.title')} <br/>
                        <span className="text-emerald-500">{t('hero.title.accent')}</span>
                    </h1>
                    <p className="max-w-2xl mx-auto text-lg text-slate-500 dark:text-emerald-500/40 font-medium leading-relaxed">
                        {t('hero.subtitle')}
                    </p>
                </div>

                <div className="max-w-3xl mx-auto space-y-8">
                    <div className="relative group">
                        <input 
                            type="text" 
                            placeholder={t('search.placeholder')}
                            className="w-full h-16 pl-14 pr-6 bg-slate-100 dark:bg-dark-card border-2 border-slate-200 dark:border-dark-border rounded-2xl font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all shadow-xl"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="fas fa-search absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 dark:text-emerald-500/40 group-focus-within:text-emerald-500 transition-colors"></i>
                        {searching && <div className="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>}
                    </div>

                    <div className="flex flex-col gap-4 bg-white dark:bg-dark-card p-4 rounded-2xl border border-slate-200 dark:border-dark-border shadow-sm">
                        {/* Category drill-down (#63): breadcrumb + L2/L3 filter chips
                            within the active department. Shown once a department is
                            picked (via the Category dropdown below or the discovery
                            tiles); it refines the selection to subcategories. */}
                        {activeCategory !== 'All' && activeL1Id && (
                            <div className="flex flex-col gap-2 w-full">
                                <CategoryBreadcrumb categories={categories} selectedId={activeCategory} onSelect={setActiveCategory} />
                                <CategoryFilterBar
                                    categories={categories}
                                    l1Id={activeL1Id}
                                    activeCategoryId={activeCategory}
                                    onSelect={(id) => setActiveCategory(id)}
                                    onClear={() => setActiveCategory('All')}
                                />
                            </div>
                        )}
                        {/* Unified filter controls: Category · Brand · Retailer · Sort.
                            One consistent row so all four read as a single group; Sort
                            pushes to the right on wide screens, wraps below on narrow. */}
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                            {/* Category single-select dropdown (department entry point) */}
                            <CategoryFilterDropdown
                                categories={categories}
                                activeCategoryId={activeCategory}
                                onSelect={(id) => setActiveCategory(id)}
                                onClear={() => setActiveCategory('All')}
                            />
                            {/* Brand multi-select dropdown */}
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[10px] font-black uppercase text-slate-400 mr-2">{t('filter.brand')}:</span>
                                <div className="relative" ref={brandDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setBrandDropdownOpen(o => !o)}
                                        aria-haspopup="true"
                                        aria-expanded={brandDropdownOpen}
                                        aria-label={t('filter.brand')}
                                        className={`px-4 py-1.5 rounded-lg font-bold text-xs transition-all flex items-center gap-2 ${activeBrands.length > 0 ? 'bg-emerald-500 text-dark' : 'bg-slate-50 dark:bg-dark-border/30 text-slate-500 dark:text-emerald-500/40 hover:bg-emerald-500/5'}`}
                                    >
                                        {brandButtonLabel()}
                                        <i className={`fas fa-chevron-down text-[9px] transition-transform ${brandDropdownOpen ? 'rotate-180' : ''}`}></i>
                                    </button>
                                    {brandDropdownOpen && (
                                        <div role="group" aria-label={t('filter.brand')} className="absolute z-20 top-full left-0 mt-1 w-56 bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl shadow-xl max-h-64 overflow-y-auto">
                                            {activeBrands.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => { setActiveBrands([]); setBrandDropdownOpen(false); }}
                                                    className="w-full px-4 py-2 text-left text-xs font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center gap-2 border-b border-slate-100 dark:border-dark-border"
                                                >
                                                    <i className="fas fa-times"></i>{t('filter.clearFilter')}
                                                </button>
                                            )}
                                            {sortedBrands.map(brand => (
                                                <label key={brand.id} className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 dark:hover:bg-dark-border/30 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={activeBrands.includes(brand.id)}
                                                        onChange={() => toggleBrand(brand.id)}
                                                        className="accent-emerald-500"
                                                    />
                                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{brand.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {activeBrands.length > 0 && (
                                    <button type="button" onClick={() => setActiveBrands([])} className="px-3 py-1.5 rounded-lg font-bold text-xs transition-all bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 flex items-center gap-1">
                                        <i className="fas fa-times text-[10px]"></i>{t('filter.clearFilter')}
                                    </button>
                                )}
                            </div>
                            {/* Retailer multi-select dropdown */}
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[10px] font-black uppercase text-slate-400 mr-2">{t('filter.retailer')}:</span>
                                <div className="relative" ref={retailerDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setRetailerDropdownOpen(o => !o)}
                                        aria-haspopup="true"
                                        aria-expanded={retailerDropdownOpen}
                                        aria-label={t('filter.retailer')}
                                        className={`px-4 py-1.5 rounded-lg font-bold text-xs transition-all flex items-center gap-2 ${activeRetailers.length > 0 ? 'bg-emerald-500 text-dark' : 'bg-slate-50 dark:bg-dark-border/30 text-slate-500 dark:text-emerald-500/40 hover:bg-emerald-500/5'}`}
                                    >
                                        {retailerButtonLabel()}
                                        <i className={`fas fa-chevron-down text-[9px] transition-transform ${retailerDropdownOpen ? 'rotate-180' : ''}`}></i>
                                    </button>
                                    {retailerDropdownOpen && (
                                        <div role="group" aria-label={t('filter.retailer')} className="absolute z-20 top-full left-0 mt-1 w-56 bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl shadow-xl max-h-64 overflow-y-auto">
                                            {activeRetailers.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => { setActiveRetailers([]); setRetailerDropdownOpen(false); }}
                                                    className="w-full px-4 py-2 text-left text-xs font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center gap-2 border-b border-slate-100 dark:border-dark-border"
                                                >
                                                    <i className="fas fa-times"></i>{t('filter.clearFilter')}
                                                </button>
                                            )}
                                            {sortedRetailers.length === 0 && (
                                                <p className="px-4 py-3 text-xs font-bold text-slate-400">{t('filter.allRetailers')}</p>
                                            )}
                                            {sortedRetailers.map(retailer => (
                                                <label key={retailer.id} className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 dark:hover:bg-dark-border/30 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={activeRetailers.includes(retailer.id)}
                                                        onChange={() => toggleRetailer(retailer.id)}
                                                        className="accent-emerald-500"
                                                    />
                                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{retailer.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {activeRetailers.length > 0 && (
                                    <button type="button" onClick={() => setActiveRetailers([])} className="px-3 py-1.5 rounded-lg font-bold text-xs transition-all bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 flex items-center gap-1">
                                        <i className="fas fa-times text-[10px]"></i>{t('filter.clearFilter')}
                                    </button>
                                )}
                            </div>
                            {/* Sort — same row, right-aligned on wide screens */}
                            <div className="flex items-center gap-2 md:ml-auto">
                                <span className="text-[10px] font-black uppercase text-slate-400 mr-2">{t('filter.sortBy')}:</span>
                                <div className="relative">
                                    <select
                                        aria-label={t('filter.sortBy')}
                                        className="appearance-none bg-slate-50 dark:bg-dark-border/30 text-slate-900 dark:text-white text-xs font-bold rounded-lg pl-4 pr-9 py-1.5 outline-none cursor-pointer focus:ring-2 focus:ring-emerald-500"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value as 'brand' | 'price')}
                                    >
                                        <option value="brand">{t('filter.sortName')}</option>
                                        <option value="price">{t('filter.sortPrice')}</option>
                                    </select>
                                    <i className="fas fa-chevron-down text-[9px] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-16">
                {!isQueryActive ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center space-y-10">
                        <div className="space-y-4">
                            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 text-2xl"><i className="fas fa-magnifying-glass"></i></div>
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">{t('search.promptTitle')}</h3>
                            <p className="max-w-md mx-auto text-slate-500 dark:text-slate-400 font-medium">{t('search.promptSubtitle')}</p>
                        </div>
                        {/* Department tiles — browse the taxonomy without typing (#63 AC1). */}
                        <CategoryTiles categories={categories} onSelect={(l1Id) => setActiveCategory(l1Id)} />
                    </div>
                ) : (products.length === 0 && !searching) ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center space-y-4">
                        <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-dark-border flex items-center justify-center text-slate-400 text-2xl"><i className="fas fa-box-open"></i></div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                            {activeCategory !== 'All' ? 'Keine Produkte in dieser Kategorie gefunden' : t('search.noResultsTitle')}
                        </h3>
                        <p className="max-w-md text-slate-500 dark:text-slate-400 font-medium">{t('search.noResultsSubtitle')}</p>
                    </div>
                ) : (
                  <>
                {(Object.entries(groupedProducts) as [string, CanonicalProduct[]][]).map(([catName, prods]) => (
                    <div key={catName} className="space-y-8">
                        <div className="flex items-center gap-4">
                            <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-widest">{catName}</h3>
                            <div className="flex-grow h-px bg-slate-200 dark:bg-dark-border"></div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {prods.map(p => (
                                <div key={p.id} className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-[2rem] p-6 hover:border-emerald-500/50 transition-all group product-card-shadow relative">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black uppercase text-slate-400 dark:text-emerald-500/30 tracking-widest">{catName}</span>
                                            <h4 className="text-lg font-black text-slate-900 dark:text-white group-hover:text-emerald-500 leading-tight">{p.name}</h4>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase">
                                                {brands.find(b => b.id === p.brandId)?.name || 'Generic Brand'}
                                            </p>
                                            <div className="pt-1 flex items-center gap-2 flex-wrap">{renderPrice(p)}{renderProspektBadge(p.id)}</div>
                                        </div>
                                        <button onClick={() => handleAddItem(p.id)} className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-dark-border text-slate-500 dark:text-emerald-500/70 flex items-center justify-center hover:bg-emerald-500 hover:text-dark transition-all"><i className="fas fa-plus"></i></button>
                                    </div>
                                    <div className="pt-4 border-t border-slate-50 dark:border-dark-border/50 flex items-center justify-between">
                                        <button
                                            onClick={() => handleViewDeals(p)}
                                            className="text-sm font-bold text-emerald-500 hover:text-emerald-400 transition-colors"
                                        >
                                            View Deals
                                        </button>
                                        <span className="text-[11px] text-slate-900 dark:text-emerald-400 font-black px-2 py-0.5 bg-emerald-500/5 rounded-lg border border-emerald-500/10 uppercase">
                                            {formatPackageSize(readPackageSize(p))}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                
                {hasMore && (
                    <div className="flex flex-col items-center gap-3 py-8">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {t('search.showing')} {products.length} {t('search.of')} {total}
                        </p>
                        <button
                            onClick={() => runSearch(page + 1, true)}
                            disabled={searching}
                            className="px-8 py-3 bg-emerald-500 text-dark font-black rounded-2xl shadow-lg shadow-emerald-500/20 hover:bg-emerald-400 transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            <i className="fas fa-chevron-down"></i> {t('search.loadMore')}
                        </button>
                    </div>
                )}
                  </>
                )}
            </section>

            {/* Product Detail Modal */}
            {detailProduct && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-dark/80 backdrop-blur-sm" onClick={() => setDetailProduct(null)}></div>
                    <div className="relative bg-white dark:bg-dark-card w-full max-w-lg rounded-[2.5rem] border border-slate-200 dark:border-dark-border shadow-2xl overflow-hidden motion-safe:animate-zoomIn">
                        <div className="p-8 border-b border-slate-50 dark:border-dark-border/50 flex justify-between items-start">
                            <div>
                                <h3 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">{detailProduct.name}</h3>
                                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-1">
                                    {brands.find(b => b.id === detailProduct.brandId)?.name || 'Generic'} • {formatPackageSize(readPackageSize(detailProduct))}
                                </p>
                                {/* #287: leaflet deal badge in the product detail view */}
                                <div className="mt-2">{renderProspektBadge(detailProduct.id)}</div>
                            </div>
                            <button onClick={() => setDetailProduct(null)} className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-dark-border text-slate-400 hover:text-white flex items-center justify-center transition-all"><i className="fas fa-times"></i></button>
                        </div>
                        <div className="p-8 space-y-6">
                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Regional Price Comparison</h4>
                            <div className="space-y-3">
                                {productOffers.length === 0 ? (
                                    <div className="py-8 text-center text-slate-500 font-medium">No active regional offers found for this item.</div>
                                ) : productOffers.map(({offer, rp, retailer}) => (
                                    <div key={offer.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-dark-border/30 rounded-2xl border border-slate-100 dark:border-dark-border/50 group hover:border-emerald-500/50 transition-all">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-white dark:bg-dark-card rounded-xl flex items-center justify-center text-emerald-500 font-black shadow-sm">
                                                {retailer?.name[0].toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="font-black text-slate-900 dark:text-white">{retailer?.name}</div>
                                                <div className="text-[10px] font-bold text-slate-400">{rp.packageSizeValue}{rp.packageSizeUnit} Pack</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-black text-emerald-500">€{(offer.priceCents/100).toFixed(2)}</div>
                                            {offer.isPromotion === 1 && <div className="text-[8px] font-black bg-red-500 text-white px-1 rounded inline-block uppercase">PROMO</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button 
                                onClick={() => { handleAddItem(detailProduct.id); setDetailProduct(null); }}
                                className="w-full py-4 bg-emerald-500 text-dark font-black rounded-2xl shadow-xl shadow-emerald-500/20 hover:bg-emerald-400 transition-all flex items-center justify-center gap-2"
                            >
                                <i className="fas fa-plus"></i> Add to active list
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Action Button - Opens Drawer */}
            {selectedList && listItems.length > 0 && (
                <div className="fixed bottom-8 right-8 z-40">
                    <button 
                        onClick={() => setIsListDrawerOpen(true)} 
                        className="flex items-center gap-3 px-6 py-4 bg-emerald-500 text-dark font-black rounded-2xl shadow-2xl shadow-emerald-500/40 hover:-translate-y-1 transition-all"
                    >
                        <i className="fas fa-list-check"></i>
                        <span>{listItems.length} items collected</span>
                    </button>
                </div>
            )}

            {/* Shopping List Drawer (kept for completeness as no changes requested there) */}
            <div className={`fixed inset-0 z-[70] transition-all duration-500 ${isListDrawerOpen ? 'visible' : 'invisible'}`}>
                <div className={`absolute inset-0 bg-dark/60 backdrop-blur-sm transition-opacity duration-500 ${isListDrawerOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setIsListDrawerOpen(false)}></div>
                <div className={`absolute top-0 right-0 h-full w-full max-w-md bg-white dark:bg-dark-card border-l border-slate-200 dark:border-dark-border shadow-2xl transition-transform duration-500 ease-out transform ${isListDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="h-full flex flex-col">
                        <div className="p-8 border-b border-slate-50 dark:border-dark-border/50 flex justify-between items-center bg-slate-50/50 dark:bg-dark/20">
                            <div>
                                <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">Active List</h2>
                                <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">{selectedList?.name}</p>
                            </div>
                            <button onClick={() => setIsListDrawerOpen(false)} className="w-10 h-10 rounded-xl bg-white dark:bg-dark-border text-slate-400 hover:text-emerald-500 flex items-center justify-center transition-all shadow-sm"><i className="fas fa-chevron-right"></i></button>
                        </div>

                        <div className="flex-grow overflow-y-auto p-8 space-y-6">
                            {listItems.map(item => {
                                const prod = productsById[item.canonicalProductId];
                                const brand = brands.find(b => b.id === prod?.brandId);
                                return (
                                    <div key={item.id} className="flex items-center gap-4 group">
                                        <div className="w-14 h-14 bg-slate-50 dark:bg-dark-border/30 rounded-2xl flex items-center justify-center text-emerald-500/30 font-black shadow-inner border border-slate-100 dark:border-dark-border/50">
                                            <i className="fas fa-tag"></i>
                                        </div>
                                        <div className="flex-grow min-w-0">
                                            <div className="font-black text-slate-900 dark:text-white truncate">{prod?.name || 'Unknown Product'}</div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{brand?.name || 'Generic Brand'}</div>
                                            {/* #287: leaflet deal badge on shopping-list rows */}
                                            <div className="pt-0.5">{renderProspektBadge(item.canonicalProductId)}</div>
                                        </div>
                                        <div className="flex items-center gap-3 bg-slate-50 dark:bg-dark-border/50 px-2 py-1.5 rounded-xl border border-slate-100 dark:border-dark-border/50">
                                            <button onClick={() => handleUpdateQuantity(item.id, -1)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-white dark:bg-dark-card text-slate-400 hover:text-red-500 transition-all"><i className="fas fa-minus text-[10px]"></i></button>
                                            <span className="text-xs font-black min-w-[1rem] text-center">{item.quantity}</span>
                                            <button onClick={() => handleUpdateQuantity(item.id, 1)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-white dark:bg-dark-card text-slate-400 hover:text-emerald-500 transition-all"><i className="fas fa-plus text-[10px]"></i></button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="p-8 bg-slate-50/50 dark:bg-dark/40 border-t border-slate-50 dark:border-dark-border/50 space-y-3">
                            <button
                                onClick={handleOptimize}
                                disabled={listItems.length === 0 || isOptimizing}
                                className="w-full h-16 bg-emerald-500 text-dark font-black rounded-2xl shadow-2xl shadow-emerald-500/30 hover:bg-emerald-400 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                            >
                                {isOptimizing ? <><i className="fas fa-spinner animate-spin"></i> Finding best deals...</> : <><i className="fas fa-bolt"></i> Optimize Multi-Store Plan</>}
                            </button>
                            {/* Single-market comparison — "which one market is cheapest for
                                the whole basket" (components/BasketComparison.tsx). */}
                            <button
                                onClick={() => selectedList && navigate(`/compare/${selectedList.id}`)}
                                disabled={listItems.length === 0}
                                className="w-full h-12 bg-white dark:bg-dark-card text-emerald-600 dark:text-emerald-500 font-black rounded-2xl border border-emerald-500/30 hover:bg-emerald-500/10 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                            >
                                <i className="fas fa-scale-balanced" aria-hidden="true"></i> {t('compare.cta')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* #287: prospekt offer details bottom sheet (opened via the badge) */}
            {prospektSheet && (
                <ProspektOfferSheet
                    offer={prospektSheet}
                    retailerName={retailers.find(r => r.id === prospektSheet.retailerId)?.name || prospektSheet.retailerId}
                    onClose={() => setProspektSheet(null)}
                />
            )}

            {/* #26 + #147: out-of-radius prompt. The resolved store list is fed to
                the optimiser as a one-off filter for this run (favourites-first
                with fallback handled inside generatePlan). */}
            {oorOpen && currentUser && (
                <OutOfRadiusModal
                    user={currentUser}
                    distanceKm={oorDistance}
                    onResolve={(stores) => { setOorOpen(false); runPlan(stores); }}
                    onUpdateUser={(u) => onUpdateUser?.(u)}
                    onCancel={() => setOorOpen(false)}
                />
            )}
        </div>
    );
};

export default Dashboard;
